import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import { CateringDeclineReasons } from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import YourCart from 'pages/cart/your-cart';
import { IServerCateringOrder } from 'remote/queries/order';
import { CateringStatus, OrderStatus } from 'state/order/types';

import { CateringConfirmationModal, ModalType } from './catering-confirmation-modal';
import { SelectionValue } from './catering-confirmation-modal/decline-modal-body';
import { CateringOrderDetails } from './catering-order-details/catering-order-details';
import { IUpdateOrderStatusInput } from './types';

interface IOrderConfirmationContentProps {
  serverOrder: IServerCateringOrder | null;
  cateringStatus: OrderStatus | null;
  onStoreOrderStatusUpdate: (input: IUpdateOrderStatusInput, reason?: SelectionValue) => void;
}
const desktopGridAreaWithOrderConfirmed = `'cateringOrderConfirmationHeader cateringOrderConfirmationHeader' 'orderAcceptedOrDeclined orderAcceptedOrDeclined' 'orderDetails cart' 'acceptDecline acceptDecline';`;
const desktopGridAreaWithoutOrderConfirmed = `'cateringOrderConfirmationHeader cateringOrderConfirmationHeader' 'orderDetails cart' 'acceptDecline acceptDecline';`;
const OrderConfirmationContentWrapper = styled.div<{ orderConfirmed: boolean }>`
  display: grid;
  width: 100%;
  padding: 2rem 1rem;
  grid-auto-rows: min-content;
  grid-template-areas: ${p =>
    `'cateringOrderConfirmationHeader' ${
      p.orderConfirmed ? "'orderAcceptedOrDeclined'" : ''
    } 'cart'  'orderDetails' 'acceptDecline'`};
  ${p => Styles.desktop`
    grid-template-areas: ${
      p.orderConfirmed ? desktopGridAreaWithOrderConfirmed : desktopGridAreaWithoutOrderConfirmed
    }
    grid-template-columns: 1fr 1fr;
  `}
  background-color: ${Styles.color.background};
  row-gap: 40px;
  column-gap: 12px;
  > div h2 {
    text-transform: none;
    font-size: 1.375rem;
    line-height: 1.375rem;
  }
`;

const CateringOrderConfirmationHeader = styled.h2`
  grid-area: cateringOrderConfirmationHeader;
  text-align: center;
  font-size: 1.375rem;
`;
const ButtonsContainer = styled.div`
  display: grid;
  row-gap: 8px;
  margin: 32px 0 35px;
  grid-area: buttons;
  justify-content: center;
  ${Styles.desktop`
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;
  `}
`;
const YourCartWrapper = styled(YourCart)`
  grid-area: cart;
`;

const AcceptOrDeclineSection = styled.div`
  grid-area: acceptDecline;
`;
const AcceptOrDeclineSectionDetails = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 30px;
  justify-content: center;
  align-items: center;
  background-color: ${Styles.color.white};
  box-shadow: ${Styles.boxShadow};
  border-radius: ${Styles.borderRadius};
`;
const AcceptOrDeclineSectionHeader = styled.h2`
  margin: 0 0 20px 0;
`;
const DeadlineDetails = styled.div`
  text-align: center;
  width: 100%;
`;
const OrderAcceptedOrDeclined = styled.div`
  grid-area: orderAcceptedOrDeclined;
`;
const CateringOrderStatus = styled.div`
  background-color: ${Styles.color.white};
  box-shadow: ${Styles.boxShadow};
  border-radius: ${Styles.borderRadius};
  padding-left: 50px;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  & > p {
    font-size: 1rem;
    font-weight: bold;
    margin: 0;
  }
`;

export const StoreOrderConfirmationPageContent: FC<IOrderConfirmationContentProps> = (
  props: IOrderConfirmationContentProps
) => {
  const onAccept = () => {
    onStoreOrderStatusUpdate({ status: CateringStatus.STORE_APPROVED });
  };
  const onDecline = (reason: SelectionValue) => {
    onStoreOrderStatusUpdate({ status: CateringStatus.STORE_DECLINED }, reason);
  };
  const { formatMessage, formatTime, formatDate } = useIntl();
  const [AcceptModal, showAcceptModal] = useDialogModal<
    {},
    { type: ModalType; pickupTime?: string }
  >({
    Component: CateringConfirmationModal,
    onConfirm: onAccept,
  });
  const [DeclineModal, showDeclineModal] = useDialogModal<
    {},
    { type: ModalType; onDecline?: (reason: SelectionValue) => void }
  >({
    Component: CateringConfirmationModal,
  });
  const { onStoreOrderStatusUpdate, serverOrder } = props;
  if (!serverOrder) {
    return null;
  }

  if (!serverOrder.catering || !serverOrder.status) {
    return <div>Order {serverOrder.rbiOrderId} is not a catering order</div>;
  }
  const { cateringStatus } = props;
  const isApproved = cateringStatus === OrderStatus.CATERING_SUCCESSFUL;
  const orderConfirmed = isApproved || cateringStatus === OrderStatus.CATERING_DECLINED;
  const createdAtTimeString = formatTime(new Date(serverOrder.createdAt), {
    format: 'h:mm a',
  });
  const createdAtDateString = formatDate(serverOrder.createdAt, {
    format: 'MMMM dd, yyyy',
  });
  const pickupTime = serverOrder.catering.pickupTime
    ? serverOrder.catering.pickupTime.replace(/\./g, '').replace(/0{3,}/g, '00')
    : '';

  const formatPickupDate = formatDate(pickupTime, { format: 'MMMM dd, yyyy' });
  const formatPickupTime = formatTime(new Date(pickupTime), { format: 'h:mm a' });

  const declineReasonOptions = {
    [CateringDeclineReasons.CATERING_ORDER_TIME_OUT]: formatMessage({ id: 'cateringOrderTimeout' }),
    [CateringDeclineReasons.INTERNAL_ERROR]: formatMessage({ id: 'internalError' }),
    [CateringDeclineReasons.INVENTORY]: formatMessage({ id: 'inventory' }),
    [CateringDeclineReasons.STAFFING]: formatMessage({ id: 'staffing' }),
    [CateringDeclineReasons.TIMING]: formatMessage({ id: 'timing' }),
  };
  const declineReason = declineReasonOptions[serverOrder.catering.declineReason];

  return (
    <OrderConfirmationContentWrapper orderConfirmed={orderConfirmed}>
      <CateringOrderConfirmationHeader>
        {formatMessage({ id: 'cateringOrder' })} #
        {serverOrder.posOrderId || serverOrder.rbiOrderId.toUpperCase()}
      </CateringOrderConfirmationHeader>
      {orderConfirmed && (
        <OrderAcceptedOrDeclined>
          <CateringOrderStatus>
            <p>{formatMessage({ id: isApproved ? 'orderAccepted' : 'orderDeclined' })}</p>
            <p>
              {isApproved ? (
                formatMessage(
                  { id: 'acceptedCateringOrder' },
                  { date: formatPickupDate, time: formatPickupTime, serviceMode: 'pickup' }
                )
              ) : (
                <>
                  {formatMessage({ id: 'declineReason' })}: {declineReason}
                </>
              )}
            </p>
          </CateringOrderStatus>
        </OrderAcceptedOrDeclined>
      )}
      <CateringOrderDetails
        customerName={serverOrder.cart.customerName}
        pickupAtTime={formatPickupTime}
        pickupAtDate={formatPickupDate}
        phoneNumber={serverOrder.cart.userDetails.phoneNumber}
        email={serverOrder.cart.userDetails.email}
      />
      <YourCartWrapper
        readOnly
        serverOrderCartEntries={serverOrder.cart.cartEntries}
        serverOrder={serverOrder}
        showAddItemsBtn={false}
        showAddExtrasBtn={false}
      />
      {!orderConfirmed && (
        <AcceptOrDeclineSection>
          <AcceptOrDeclineSectionHeader>
            {formatMessage({ id: 'acceptOrDecline' })}
          </AcceptOrDeclineSectionHeader>
          <AcceptOrDeclineSectionDetails>
            <DeadlineDetails>
              {formatMessage({ id: 'pleaseMakeSelectionInXHours' }, { hour: '24' })}
            </DeadlineDetails>
            <DeadlineDetails>
              {`${formatMessage({
                id: 'orderPlacedAt',
              })} ${createdAtTimeString}, ${createdAtDateString}`}
            </DeadlineDetails>
            <ButtonsContainer>
              <ActionButton onClick={showAcceptModal}>
                {formatMessage({ id: 'accept' })}
              </ActionButton>
              <ActionButton onClick={showDeclineModal}>
                {formatMessage({ id: 'decline' })}
              </ActionButton>
            </ButtonsContainer>
          </AcceptOrDeclineSectionDetails>
        </AcceptOrDeclineSection>
      )}
      <AcceptModal pickupTime={pickupTime} type={ModalType.ACCEPT} />
      <DeclineModal type={ModalType.DECLINE} onDecline={onDecline} />
    </OrderConfirmationContentWrapper>
  );
};
